import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ToastContainer from '../../components/common/ToastContainer';
import { useProducts } from '../../hooks/useProducts';
import { useToast } from '../../hooks/useToast';

export default function Catalogo() {
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [visibleCategories, setVisibleCategories] = useState<number>(6);
  const [showDropdown, setShowDropdown] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState<{ [key: string]: number }>({});
  const [touchStart, setTouchStart] = useState<{ [key: string]: number }>({});
  const [touchEnd, setTouchEnd] = useState<{ [key: string]: number }>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const { products: allProducts, loading, error } = useProducts();
  const { toasts, showToast, removeToast } = useToast();

  // Função para calcular quantas categorias cabem na largura disponível
  const calculateVisibleCategories = () => {
    if (!containerRef.current || categories.length === 0) return;
    
    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const gap = 8; // gap-2 = 0.5rem = 8px
    const moreButtonWidth = 60; // largura mínima do botão "+"
    
    let totalWidth = 0;
    let visibleCount = 0;
    
    // Primeiro, calcular largura de todos os botões renderizados
    const buttonWidths: number[] = [];
    for (let i = 0; i < categories.length; i++) {
      const button = buttonsRef.current[i];
      if (button) {
        buttonWidths[i] = button.offsetWidth;
      } else {
        // Larguras estimadas mais precisas baseadas no tamanho do texto
        const textLength = categories[i]?.name.length || 10;
        buttonWidths[i] = Math.max(80, Math.min(160, textLength * 8 + 32));
      }
    }
    
    // Calcular quantos botões cabem
    for (let i = 0; i < categories.length; i++) {
      const buttonWidth = buttonWidths[i] + gap;
      
      // Se adicionar este botão ultrapassar a largura disponível
      if (totalWidth + buttonWidth > containerWidth) {
        // Se não é o último botão, verificar se sobra espaço para o botão "+"
        if (i < categories.length - 1) {
          if (totalWidth + moreButtonWidth <= containerWidth) {
            // Há espaço para o botão "+"
            break;
          } else {
            // Não há espaço nem para o botão "+", remover último botão
            visibleCount = Math.max(1, visibleCount - 1);
            break;
          }
        } else {
          // É o último botão e não cabe
          break;
        }
      }
      
      totalWidth += buttonWidth;
      visibleCount++;
    }
    
    // Garantir que pelo menos 1 categoria seja mostrada
    visibleCount = Math.max(1, visibleCount);
    
    // Se todos os botões cabem, mostrar todos
    if (visibleCount >= categories.length) {
      setVisibleCategories(categories.length);
    } else {
      setVisibleCategories(visibleCount);
    }
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtra apenas produtos ativos (ACTIVE = true ou "Sim")
  const activeProducts = allProducts.filter(product => product.ACTIVE);

  // Gera categorias dinamicamente a partir dos produtos
  const categories = useMemo(() => {
    const uniqueCategories = new Set(['todos']);
    activeProducts.forEach(product => {
      if (product.CATEGORY && product.CATEGORY.trim()) {
        uniqueCategories.add(product.CATEGORY.toLowerCase().trim());
      }
    });
    
    // Função para formatar o nome da categoria
    const formatCategoryName = (category: string): string => {
      if (category === 'todos') return 'Todas as Categorias';
      
      const categoryMap: { [key: string]: string } = {
        'motor': 'Motor',
        'suspensao': 'Suspensão',
        'freios': 'Freios',
        'eletrica': 'Elétrica',
        'carroceria': 'Carroceria',
        'transmissao': 'Transmissão',
        'acessorios': 'Acessórios',
        'iluminacao': 'Iluminação',
        'interior': 'Interior'
      };
      
      return categoryMap[category] || category.charAt(0).toUpperCase() + category.slice(1);
    };

    return Array.from(uniqueCategories).map(cat => ({
      id: cat,
      name: formatCategoryName(cat)
    }));

  }, [activeProducts]);

  // Reordenar categorias para mostrar a selecionada logo após "Todas as Categorias"
  const orderedCategories = useMemo(() => {
    if (!categories.length || selectedCategory === 'todos') {
      return categories;
    }
    
    const selectedCat = categories.find(cat => cat.id === selectedCategory);
    const otherCats = categories.filter(cat => cat.id !== selectedCategory && cat.id !== 'todos');
    const todosCat = categories.find(cat => cat.id === 'todos');
    
    if (selectedCat && todosCat) {
      return [todosCat, selectedCat, ...otherCats];
    }
    
    return categories;
  }, [categories, selectedCategory]);

  // Recalcular quando as categorias ou tamanho da tela mudar
  useEffect(() => {
    const timer = setTimeout(() => {
      calculateVisibleCategories();
    }, 100); // Delay para garantir que os elementos foram renderizados
    
    // Tentar novamente após um tempo maior caso os elementos ainda não estejam prontos
    const timer2 = setTimeout(() => {
      calculateVisibleCategories();
    }, 500);
    
    const handleResize = () => {
      calculateVisibleCategories();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      window.removeEventListener('resize', handleResize);
    };
  }, [orderedCategories]);

  const filteredProducts = activeProducts.filter(product => {
    const matchesCategory = selectedCategory === 'todos' || 
      product.CATEGORY.toLowerCase().trim() === selectedCategory.toLowerCase();
    const matchesSearch = product.TITLE.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.DESCRIPTION.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Função para obter o nome formatado da categoria selecionada
  const getSelectedCategoryName = () => {
    const category = categories.find(cat => cat.id === selectedCategory);
    return category ? category.name : 'Todas as Categorias';
  };

  // Lógica de paginação
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // Reset página quando filtros mudarem
  useMemo(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm, itemsPerPage]);

  // Função para mudar página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Gerar números de páginas para exibir
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">A melhor qualidade para o seu veículo</h1>
          <p className="text-xl text-gray-300">
            Explore o nosso catálogo e encontre as melhores peças e acessórios do mercado.
          </p>
        </div>
      </section>

      {/* Filtros e Busca */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6 items-center">
            {/* Busca */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Buscar peças..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Filtro de Categoria Responsivo */}
            <div ref={containerRef} className="flex flex-1 flex-wrap gap-2 relative">
              {orderedCategories.slice(0, visibleCategories).map((category, index) => (
                <button
                  key={category.id}
                  ref={(el) => {
                    buttonsRef.current[index] = el;
                  }}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setShowDropdown(false);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {category.name}
                </button>
              ))}
              
              {orderedCategories.length > visibleCategories && (
                <div className="relative">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center gap-1"
                  >
                    <span>+{orderedCategories.length - visibleCategories}</span>
                    <i className={`ri-arrow-down-s-line transition-transform ${
                      showDropdown ? 'rotate-180' : ''
                    }`}></i>
                  </button>
                  
                  {/* Dropdown com categorias extras */}
                  {showDropdown && (
                    <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-48 z-20 max-h-60 overflow-y-auto">
                      {orderedCategories.slice(visibleCategories).map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            setSelectedCategory(category.id);
                            setShowDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-sm ${
                            selectedCategory === category.id
                              ? 'bg-teal-50 text-teal-600 font-medium'
                              : 'text-gray-700'
                          }`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Grid de Produtos */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-gray-600">Carregando produtos...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <i className="ri-error-warning-line text-6xl text-red-400 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Erro ao carregar produtos</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-gray-600">
                  Mostrando {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} de {filteredProducts.length} produto(s)
                  {selectedCategory !== 'todos' && (
                    <span className="text-teal-600 font-medium"> para a categoria {getSelectedCategoryName()}</span>
                  )}
                </p>
                
                {/* Seletor de itens por página */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Mostrar:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-600">por página</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
                {paginatedProducts.map((product) => {
                  // Carrossel de imagens
                  const images = (product.IMAGE || '').split(';').map(img => img.trim()).filter(Boolean);
                  const currentIndex = carouselIndex[product.ID] || 0;
                  
                  return (
                    <div key={product.ID} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                      <div 
                        className="relative w-full group cursor-grab active:cursor-grabbing"
                        style={{ height: '16rem' }}
                        onTouchStart={(e) => setTouchStart({ ...touchStart, [product.ID]: e.changedTouches[0].clientX })}
                        onTouchEnd={(e) => {
                          setTouchEnd({ ...touchEnd, [product.ID]: e.changedTouches[0].clientX });
                          const start = touchStart[product.ID];
                          const end = e.changedTouches[0].clientX;
                          if (start && end) {
                            const distance = start - end;
                            const isLeftSwipe = distance > 50;
                            const isRightSwipe = distance < -50;
                            
                            if (isLeftSwipe && currentIndex < images.length - 1) {
                              setCarouselIndex({ ...carouselIndex, [product.ID]: currentIndex + 1 });
                            }
                            if (isRightSwipe && currentIndex > 0) {
                              setCarouselIndex({ ...carouselIndex, [product.ID]: currentIndex - 1 });
                            }
                          }
                        }}
                      >
                        {images.length > 0 ? (
                          <>
                            <img
                              src={`https://drive.google.com/thumbnail?id=${images[currentIndex]}`}
                              alt={product.TITLE}
                              style={{ width: '100%', height: '16rem', objectFit: 'contain', objectPosition: 'center' }}
                              loading="lazy"
                              draggable="false"
                            />
                            
                            {/* Setas de navegação - visíveis no hover (desktop) */}
                            {images.length > 1 && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setCarouselIndex({ ...carouselIndex, [product.ID]: Math.max(0, currentIndex - 1) })}
                                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block"
                                  aria-label="Imagem anterior"
                                >
                                  <i className="ri-arrow-left-s-line text-lg" />
                                </button>
                                
                                <button
                                  type="button"
                                  onClick={() => setCarouselIndex({ ...carouselIndex, [product.ID]: Math.min(images.length - 1, currentIndex + 1) })}
                                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block"
                                  aria-label="Próxima imagem"
                                >
                                  <i className="ri-arrow-right-s-line text-lg" />
                                </button>
                              </>
                            )}
                            
                            {/* Indicador de página */}
                            {images.length > 1 && (
                              <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium">
                                {currentIndex + 1} / {images.length}
                              </div>
                            )}
                            
                            {/* Bolinhas de navegação */}
                            {images.length > 1 && (
                              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                                {images.map((_, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className={`w-3 h-3 rounded-full border-2 transition-all ${currentIndex === idx ? 'bg-teal-600 border-teal-600 w-4' : 'bg-white border-gray-300'}`}
                                    onClick={() => setCarouselIndex({ ...carouselIndex, [product.ID]: idx })}
                                    aria-label={`Ir para imagem ${idx + 1}`}
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-400">
                            <i className="ri-image-line text-4xl" />
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            product.AVAILABILITY?.toLowerCase() === 'importar' 
                              ? 'bg-orange-100 text-orange-800'
                              : product.AVAILABILITY?.toLowerCase().includes('estoque') || product.AVAILABILITY?.toLowerCase().includes('disponível')
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {product.AVAILABILITY || 'Consultar'}
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.TITLE}</h3>
                        <p
                          className="text-gray-600 text-sm mb-3"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'justify'
                          }}
                        >
                          {product.DESCRIPTION}
                        </p>
                        
                        <div className="flex gap-2 mt-4">
                          {product.AVAILABILITY?.toLowerCase() === 'importar' ? (
                            <>
                              <a
                                href={product.LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 bg-teal-600 text-white rounded-lg text-center hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center justify-center px-3 py-2"
                              >
                                <span className="block leading-none">Ver detalhes</span>
                              </a>
                              <Link
                                to="/contato?assunto=Importação de Peças e Acessórios"
                                className="flex-1 bg-teal-600 text-white rounded-lg text-center hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center justify-center px-3 py-2"
                              >
                                <span className="block leading-none">Importar</span>
                              </Link>
                            </>
                          ) : (
                            <a
                              href={product.LINK}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 bg-teal-600 text-white rounded-lg text-center hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center justify-center px-3 py-2"
                            >
                              <span className="block leading-none">Ver Produto no Mercado Livre</span>
                            </a>
                          )}
                          <button
                            type="button"
                            title="Copiar link"
                            onClick={async () => {
                              const link = product.LINK || '';
                              try {
                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                  await navigator.clipboard.writeText(link);
                                } else {
                                  // Fallback for older browsers
                                  const textarea = document.createElement('textarea');
                                  textarea.value = link;
                                  textarea.style.position = 'fixed';
                                  textarea.style.left = '-9999px';
                                  document.body.appendChild(textarea);
                                  textarea.select();
                                  document.execCommand('copy');
                                  document.body.removeChild(textarea);
                                }
                                // feedback com toast
                                showToast('Link copiado para a área de transferência', 'success');
                              } catch (err) {
                                showToast('Não foi possível copiar o link', 'error');
                                console.error('Copy failed', err);
                              }
                            }}
                            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-share-line text-gray-600"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="mt-8 flex flex-col items-center justify-center gap-4">
                  <div className="text-sm text-gray-600">
                    Página {currentPage} de {totalPages}
                  </div>
                  
                  <div className="flex items-center gap-1 flex-wrap justify-center">
                    {/* Botão Anterior */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span className="hidden sm:inline">Anterior</span>
                      <span className="sm:hidden">‹</span>
                    </button>
                    
                    {/* Números das páginas */}
                    <div className="hidden sm:flex items-center gap-1">
                      {getPageNumbers().map((page, index) => (
                        <div key={index}>
                          {page === '...' ? (
                            <span className="px-3 py-2 text-gray-400">...</span>
                          ) : (
                            <button
                              onClick={() => handlePageChange(page as number)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                currentPage === page
                                  ? 'bg-teal-600 text-white'
                                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Input direto para página (mobile) */}
                    <div className="sm:hidden flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={currentPage}
                        onChange={(e) => {
                          const page = parseInt(e.target.value);
                          if (page >= 1 && page <= totalPages) {
                            handlePageChange(page);
                          }
                        }}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                      />
                      <span className="text-sm text-gray-600">de {totalPages}</span>
                    </div>
                    
                    {/* Botão Próximo */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span className="hidden sm:inline">Próximo</span>
                      <span className="sm:hidden">›</span>
                    </button>
                  </div>
                </div>
              )}

              {paginatedProducts.length === 0 && filteredProducts.length === 0 && !loading && (
                <div className="text-center py-12">
                  <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhuma peça encontrada</h3>
                  <p className="text-gray-600 mb-6">Tente ajustar os filtros ou termo de busca</p>
                  <Link
                    to="/importacao"
                    className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors inline-block whitespace-nowrap cursor-pointer"
                  >
                    Encomendar
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
